export interface MixedAudioStreamOptions {
    microphoneGain?: number;
    systemGain?: number;
}

export interface MixedAudioStreamResult {
    stream: MediaStream;
    audioContext: AudioContext;
    microphoneGainNode?: GainNode;
    systemGainNode?: GainNode;
    cleanup: () => Promise<void>;
    replaceSystemStream: (newStream: MediaStream) => Promise<void>;
    detachSystemStream: () => Promise<void>;
}

const createAudioOnlyStream = (stream: MediaStream): MediaStream | null => {
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) {
        return null;
    }
    return new MediaStream(audioTracks);
};

export const createMixedAudioStream = async (
    displayStream: MediaStream | null,
    microphoneStream: MediaStream,
    options: MixedAudioStreamOptions = {}
): Promise<MixedAudioStreamResult> => {
    if (typeof window === 'undefined') {
        throw new Error('Audio mixing kan alleen in de browser plaatsvinden.');
    }

    const audioContext = new AudioContext();
    if (audioContext.state === 'suspended') {
        await audioContext.resume();
    }

    const destination = audioContext.createMediaStreamDestination();

    const systemAudioOnlyStream = displayStream ? createAudioOnlyStream(displayStream) : null;
    const microphoneAudioOnlyStream = createAudioOnlyStream(microphoneStream);

    if (!systemAudioOnlyStream && !microphoneAudioOnlyStream) {
        await audioContext.close();
        throw new Error('Geen audiotracks gevonden om te mixen.');
    }

    let systemAudioSource: MediaStreamAudioSourceNode | null = null;
    let microphoneSource: MediaStreamAudioSourceNode | null = null;
    let microphoneGain: GainNode | undefined;

    // De systeem-gain altijd aanmaken en met de destination verbinden, óók als
    // er (nog) geen systeemstream is. Zo kan replaceSystemStream later alsnog
    // een scherm-/systeembron in de opname mixen wanneer de opname mic-only
    // begon (anders blijft die bron stil omdat er geen gain-node bestaat).
    const systemGain = audioContext.createGain();
    systemGain.gain.value = options.systemGain ?? 1;
    systemGain.connect(destination);

    if (systemAudioOnlyStream) {
        systemAudioSource = audioContext.createMediaStreamSource(systemAudioOnlyStream);
        systemAudioSource.connect(systemGain);
    }

    if (microphoneAudioOnlyStream) {
        microphoneSource = audioContext.createMediaStreamSource(microphoneAudioOnlyStream);
        microphoneGain = audioContext.createGain();
        microphoneGain.gain.value = options.microphoneGain ?? 1;
        microphoneSource.connect(microphoneGain).connect(destination);
    }

    const cleanup = async () => {
        try {
            if (systemAudioSource) {
                systemAudioSource.disconnect();
            }
            if (microphoneSource) {
                microphoneSource.disconnect();
            }
            if (systemGain) {
                systemGain.disconnect();
            }
            if (microphoneGain) {
                microphoneGain.disconnect();
            }
        } finally {
            if (audioContext.state !== 'closed') {
                await audioContext.close();
            }
        }
    };

    const replaceSystemStream = async (newStream: MediaStream) => {
        // 1. Cleanup old source
        if (systemAudioSource) {
            systemAudioSource.disconnect();
            systemAudioSource = null;
        }

        // 2. Create new source en op de (altijd aanwezige) systeem-gain hangen,
        // die al met de destination verbonden is.
        const newSystemAudioOnlyStream = createAudioOnlyStream(newStream);
        if (newSystemAudioOnlyStream) {
            systemAudioSource = audioContext.createMediaStreamSource(newSystemAudioOnlyStream);
            systemAudioSource.connect(systemGain);
        }
    };

    // Koppelt alleen de systeembron los; de microfoon blijft via microphoneGain
    // met de destination verbonden. Hiermee kan een opname naadloos doorlopen
    // als alleen-microfoon nadat de schermdeling is gestopt. De systeem-gain
    // blijft staan zodat een latere replaceSystemStream de bron weer kan mixen.
    const detachSystemStream = async () => {
        if (systemAudioSource) {
            systemAudioSource.disconnect();
            systemAudioSource = null;
        }
    };

    return {
        stream: destination.stream,
        audioContext,
        microphoneGainNode: microphoneGain,
        systemGainNode: systemGain,
        cleanup,
        replaceSystemStream,
        detachSystemStream
    };
};
